import { companyGroupLabel } from '@/lib/company-group';
import type {
  ProductInsightsCompanyRef,
  ProductInsightsEvent,
  ProductInsightsEventFormat,
  ProductInsightsEventSchool,
  ProductInsightsEventTotals,
} from '@/lib/types';

/**
 * Product Insights → Events: URL state and the pure maths the page shows.
 *
 * - URL keys (absent = default): `cycle` (SEED FAB cycle id; absent = SEED
 *   FAB's current), `tab` (`schools`; absent = events), `format`
 *   (`in_person|virtual`), `region` (id), `product` (sub-product id), `q`.
 * - `cycle`, `format` and a SINGLE `region` go to the server; `product` (CSV),
 *   several regions (CSV) and `q` are client-side views over the one fetched
 *   set.
 * - "Reach" is split by format on purpose: in person it is the distinct
 *   registrants scanned at that company; virtual it is the whole event's
 *   registrations ("Shared reach"). The two are never added together.
 * - Group (Institutions / Partners): the EMERGE company's list group when the
 *   row resolved to a company, else the upstream `entityType`
 *   (`partner` → Partners, anything else → Institutions).
 */

export type EventsTab = 'events' | 'schools';
export type EventsFormatFilter = 'all' | ProductInsightsEventFormat;

export interface EventsUrlState {
  cycleId: number | null;
  tab: EventsTab;
  format: EventsFormatFilter;
  /** Multi-select (owner 2026-10-28); `[]` = all. URL `region` is a CSV. */
  regionIds: number[];
  /** Multi-select (owner 2026-10-28); `[]` = all. URL `product` is a CSV. */
  productIds: number[];
  search: string;
}

function positiveInt(raw: string | null): number | null {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** A CSV of ids; malformed parts dropped, duplicates collapsed. An old single-id link is a one-item list. */
function positiveIntList(raw: string | null): number[] {
  if (raw === null) return [];
  const out: number[] = [];
  for (const part of raw.split(',')) {
    const id = positiveInt(part.trim());
    if (id !== null && !out.includes(id)) out.push(id);
  }
  return out;
}

export function readEventsState(params: URLSearchParams): EventsUrlState {
  const format = params.get('format');
  return {
    cycleId: positiveInt(params.get('cycle')),
    tab: params.get('tab') === 'schools' ? 'schools' : 'events',
    format: format === 'in_person' || format === 'virtual' ? format : 'all',
    regionIds: positiveIntList(params.get('region')),
    productIds: positiveIntList(params.get('product')),
    search: params.get('q') ?? '',
  };
}

/** Writes every key of `state` onto a copy of `prev`; a default is an absent key. */
export function applyEventsState(prev: URLSearchParams, state: EventsUrlState): URLSearchParams {
  const next = new URLSearchParams(prev);
  const put = (key: string, value: string | null) => {
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
  };
  put('cycle', state.cycleId === null ? null : String(state.cycleId));
  put('tab', state.tab === 'events' ? null : state.tab);
  put('format', state.format === 'all' ? null : state.format);
  put('region', state.regionIds.join(','));
  put('product', state.productIds.join(','));
  put('q', state.search.trim() === '' ? null : state.search);
  return next;
}

/** Every page filter back to its default (cycle, format, region, product, search); the tab stays. */
export function resetEventsFilters(state: EventsUrlState): EventsUrlState {
  return { ...state, cycleId: null, format: 'all', regionIds: [], productIds: [], search: '' };
}

/** True while any filter differs from its default (the chip row shows then). */
export function hasEventsFilters(state: EventsUrlState): boolean {
  return (
    state.cycleId !== null ||
    state.format !== 'all' ||
    state.regionIds.length > 0 ||
    state.productIds.length > 0 ||
    state.search.trim() !== ''
  );
}

/* --- formatting ----------------------------------------------------------- */

const DAY_MONTH = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

function parseDay(iso: string | null): Date | null {
  if (iso === null) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "12 Oct 2026", or "12 – 14 Oct 2026" / "30 Sept – 2 Oct 2026" for a multi-day event. */
export function eventDateText(startDate: string | null, endDate: string | null): string {
  const start = parseDay(startDate);
  if (start === null) return '—';
  const end = parseDay(endDate);
  if (end === null || end.getTime() <= start.getTime()) return DAY_MONTH_YEAR.format(start);
  if (start.getUTCFullYear() !== end.getUTCFullYear()) {
    return `${DAY_MONTH_YEAR.format(start)} – ${DAY_MONTH_YEAR.format(end)}`;
  }
  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${start.getUTCDate()} – ${DAY_MONTH_YEAR.format(end)}`;
  }
  return `${DAY_MONTH.format(start)} – ${DAY_MONTH_YEAR.format(end)}`;
}

export function eventPlaceText(event: ProductInsightsEvent): string {
  const parts = [event.city, event.country].filter(
    (v): v is string => v !== null && v.trim() !== '',
  );
  if (parts.length > 0) return parts.join(', ');
  return event.format === 'virtual' ? 'Online' : '—';
}

export const FORMAT_LABEL: Record<ProductInsightsEventFormat, string> = {
  in_person: 'In person',
  virtual: 'Virtual',
};

/* --- groups --------------------------------------------------------------- */

export interface SchoolGroupInfo {
  label: string;
  order: number;
}

function groupOf(
  company: ProductInsightsCompanyRef | null,
  entityType: ProductInsightsEventSchool['entityType'],
): SchoolGroupInfo {
  if (company?.group) return { label: companyGroupLabel(company.group.name), order: company.group.sortOrder };
  return entityType === 'partner'
    ? { label: 'Partners', order: Number.MAX_SAFE_INTEGER }
    : { label: 'Institutions', order: -1 };
}

/** Buckets rows by group label (Institutions first), keeping each bucket's order. */
export function splitIntoGroups<T>(
  rows: readonly T[],
  infoOf: (row: T) => SchoolGroupInfo,
): { label: string; rows: T[] }[] {
  const byLabel = new Map<string, { label: string; order: number; rows: T[] }>();
  for (const row of rows) {
    const info = infoOf(row);
    const bucket = byLabel.get(info.label);
    if (bucket === undefined) byLabel.set(info.label, { ...info, rows: [row] });
    else {
      bucket.rows.push(row);
      bucket.order = Math.min(bucket.order, info.order);
    }
  }
  return [...byLabel.values()]
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
    .map(({ label, rows: bucketRows }) => ({ label, rows: bucketRows }));
}

export function schoolGroup(school: ProductInsightsEventSchool): SchoolGroupInfo {
  return groupOf(school.company, school.entityType);
}

export function schoolDisplayName(school: ProductInsightsEventSchool): string {
  return school.company?.name ?? school.name;
}

/** A stable key for an event row; upstream may omit `eventId`. */
export function eventKey(event: ProductInsightsEvent): string {
  return event.eventId !== null ? `e:${event.eventId}` : `n:${event.name}|${event.startDate ?? ''}`;
}

/** An event's schools, most engaged first. */
export function rankedSchools(event: ProductInsightsEvent): ProductInsightsEventSchool[] {
  return [...event.schools].sort(
    (a, b) =>
      b.boothConnections - a.boothConnections ||
      b.presentationAttendees - a.presentationAttendees ||
      b.registrationsReached - a.registrationsReached ||
      schoolDisplayName(a).localeCompare(schoolDisplayName(b)),
  );
}

/* --- filtering + headline ------------------------------------------------- */

export interface ProductOption {
  id: number;
  label: string;
}

/** Sub-products present in the fetched set, by name. */
export function productOptions(events: readonly ProductInsightsEvent[]): ProductOption[] {
  const byId = new Map<number, string>();
  for (const event of events) {
    if (event.subProductId === null) continue;
    const name = event.subProductName?.trim();
    if (!byId.has(event.subProductId)) byId.set(event.subProductId, name || `Product ${event.subProductId}`);
  }
  return [...byId.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function eventMatches(event: ProductInsightsEvent, needle: string): boolean {
  if (needle === '') return true;
  const hay = [event.name, event.city, event.country, event.subProductName]
    .filter((v): v is string => v !== null)
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

/** The product filter only (search is per tab). `[]` = every product. */
export function filterByProduct(
  events: readonly ProductInsightsEvent[],
  productIds: readonly number[],
): ProductInsightsEvent[] {
  if (productIds.length === 0) return [...events];
  const wanted = new Set(productIds);
  return events.filter((e) => e.subProductId !== null && wanted.has(e.subProductId));
}

/**
 * The region filter, client-side. Only needed for SEVERAL regions: one region
 * goes to the server as `regionId` (the upstream takes one), so its totals stay
 * the server's. With 2+ the set is fetched unnarrowed and filtered here.
 */
export function filterByRegions(
  events: readonly ProductInsightsEvent[],
  regionIds: readonly number[],
): ProductInsightsEvent[] {
  if (regionIds.length < 2) return [...events];
  const wanted = new Set(regionIds);
  return events.filter((e) => e.regionId !== null && wanted.has(e.regionId));
}

export function filterBySearch(
  events: readonly ProductInsightsEvent[],
  search: string,
): ProductInsightsEvent[] {
  const needle = search.trim().toLowerCase();
  return events.filter((e) => eventMatches(e, needle));
}

export interface EventsHeadline {
  events: number;
  registrations: number;
  attendees: number;
  attendanceRate: number | null;
  boothConnections: number;
  presentationAttendees: number;
}

export function headlineFromTotals(totals: ProductInsightsEventTotals): EventsHeadline {
  return { ...totals };
}

/** Sums a narrowed list the same way the server totals the whole set. */
export function headlineFromEvents(events: readonly ProductInsightsEvent[]): EventsHeadline {
  let registrations = 0;
  let attendees = 0;
  let boothConnections = 0;
  let presentationAttendees = 0;
  for (const event of events) {
    registrations += event.registrations;
    attendees += event.attendees;
    for (const school of event.schools) {
      boothConnections += school.boothConnections;
      presentationAttendees += school.presentationAttendees;
    }
  }
  return {
    events: events.length,
    registrations,
    attendees,
    attendanceRate: registrations > 0 ? attendees / registrations : null,
    boothConnections,
    presentationAttendees,
  };
}

/* --- by school ------------------------------------------------------------ */

export interface SchoolEventEntry {
  event: ProductInsightsEvent;
  school: ProductInsightsEventSchool;
}

export interface SchoolAggregate {
  key: string;
  name: string;
  company: ProductInsightsCompanyRef | null;
  group: SchoolGroupInfo;
  events: number;
  inPersonEvents: number;
  virtualEvents: number;
  boothConnections: number;
  presentationAttendees: number;
  /** In-person reach only (distinct registrants scanned). */
  reach: number;
  /** Virtual events' registrations; shared with every school on the event. */
  sharedReach: number;
  entries: SchoolEventEntry[];
}

function schoolKey(school: ProductInsightsEventSchool): string {
  if (school.company) return `c:${school.company.id}`;
  // The SEED platform id lives in its own key space: it is NOT d_companies.id.
  if (school.upstreamCompanyId !== null) return `u:${school.upstreamCompanyId}`;
  if (school.schoolId !== null) return `s:${school.schoolId}`;
  return `n:${school.name.trim().toLowerCase()}`;
}

export function aggregateBySchool(events: readonly ProductInsightsEvent[]): SchoolAggregate[] {
  const byKey = new Map<string, SchoolAggregate>();
  for (const event of events) {
    for (const school of event.schools) {
      const key = schoolKey(school);
      let row = byKey.get(key);
      if (row === undefined) {
        row = {
          key,
          name: schoolDisplayName(school),
          company: school.company,
          group: schoolGroup(school),
          events: 0,
          inPersonEvents: 0,
          virtualEvents: 0,
          boothConnections: 0,
          presentationAttendees: 0,
          reach: 0,
          sharedReach: 0,
          entries: [],
        };
        byKey.set(key, row);
      }
      row.events += 1;
      row.boothConnections += school.boothConnections;
      row.presentationAttendees += school.presentationAttendees;
      if (school.sharedReach) {
        row.virtualEvents += 1;
        row.sharedReach += school.registrationsReached;
      } else {
        row.inPersonEvents += 1;
        row.reach += school.registrationsReached;
      }
      row.entries.push({ event, school });
    }
  }
  for (const row of byKey.values()) {
    row.entries.sort((a, b) => (b.event.startDate ?? '').localeCompare(a.event.startDate ?? ''));
  }
  return [...byKey.values()];
}

export function schoolMatches(row: SchoolAggregate, search: string): boolean {
  const needle = search.trim().toLowerCase();
  return needle === '' || row.name.toLowerCase().includes(needle);
}

/* --- sorting -------------------------------------------------------------- */

export type SortDir = 'asc' | 'desc';
export interface SortState<C extends string> {
  column: C;
  dir: SortDir;
}

export function parseSortState<C extends string>(
  columns: readonly C[],
): (raw: unknown) => SortState<C> | undefined {
  return (raw) => {
    if (typeof raw !== 'object' || raw === null) return undefined;
    const { column, dir } = raw as { column?: unknown; dir?: unknown };
    if (dir !== 'asc' && dir !== 'desc') return undefined;
    const match = columns.find((c) => c === column);
    return match === undefined ? undefined : { column: match, dir };
  };
}

/** Numbers compare numerically, strings by locale; null sorts last in either direction. */
export function compareValues(
  left: number | string | null,
  right: number | string | null,
  dir: SortDir,
): number {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  const factor = dir === 'asc' ? 1 : -1;
  if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;
  return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) * factor;
}

export function ariaSort(state: SortDir | null): 'ascending' | 'descending' | undefined {
  return state === null ? undefined : state === 'asc' ? 'ascending' : 'descending';
}
